import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    FileText,
    Plus,
    Clock,
    Trash2,
    Upload,
    ExternalLink,
    User,
    CheckCircle2,
    AlertTriangle,
    ArrowRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageHeader } from './ui/PageHeader';
import { StatusBadge } from './ui/StatusBadge';

import { uploadFile } from '../services/upload';

const ProcurementManager: React.FC = () => {
    const [procs, setProcs] = useState<any[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [title, setTitle] = useState('');
    const [desc, setDesc] = useState('');
    const [deadline, setDeadline] = useState('');
    const [initialQuotes, setInitialQuotes] = useState<any[]>([]);

    useEffect(() => { fetchProcs(); }, []);

    const fetchProcs = async () => {
        const res = await api.get('/procurements');
        setProcs(res.data);
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await api.post('/procurements', {
                title, description: desc, deadline,
                initial_quotes: initialQuotes
            });
            fetchProcs();
            setIsCreating(false);
            setTitle(''); setDesc(''); setDeadline(''); setInitialQuotes([]);
        } catch (e) { alert('Procurement creation failed'); }
    };

    const updateStatus = async (id: string, status: string) => {
        try {
            await api.patch(`/procurements/${id}/status?status=${status}`);
            fetchProcs();
        } catch (e) { alert('Status update failed'); }
    };

    const addQuoteField = () => setInitialQuotes([...initialQuotes, { amount: 0, remarks: '', timeline_days: 7, attachment_url: '' }]);

    return (
        <div className="space-y-6">
            <PageHeader title="Procurement" subtitle="Transparent vendor selection and bidding">
                <button
                    onClick={() => setIsCreating(!isCreating)}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2 hover:bg-violet-700 transition-all text-sm"
                >
                    {isCreating ? 'Cancel' : 'Start Procurement'}
                    <Plus size={18} className={cn("transition-transform", isCreating && "rotate-45")} />
                </button>
            </PageHeader>

            {isCreating && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 animate-in slide-in-from-top-2 duration-300">
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Project Title</label>
                                <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="e.g. Lift Repair - Block B" required />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Deadline</label>
                                <input type="datetime-local" value={deadline} onChange={e => setDeadline(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" required />
                            </div>
                            <div className="md:col-span-2 space-y-1">
                                <label className="text-xs font-semibold text-slate-500">Description</label>
                                <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-violet-500" placeholder="Full scope of work..." />
                            </div>
                        </div>

                        <div className="space-y-3 pt-4 border-t border-slate-100">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Initial Board Quotes</h4>
                            {initialQuotes.map((q, idx) => (
                                <div key={idx} className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-slate-50 p-4 rounded-lg items-center">
                                    <input type="number" placeholder="Amount" className="bg-white border border-slate-200 rounded-md p-2 text-sm" onChange={e => {
                                        const n = [...initialQuotes]; n[idx].amount = parseFloat(e.target.value); setInitialQuotes(n);
                                    }} required />
                                    <input placeholder="Vendor / Remarks" className="bg-white border border-slate-200 rounded-md p-2 text-sm col-span-2" onChange={e => {
                                        const n = [...initialQuotes]; n[idx].remarks = e.target.value; setInitialQuotes(n);
                                    }} required />
                                    <input type="number" placeholder="Days" className="bg-white border border-slate-200 rounded-md p-2 text-sm" onChange={e => {
                                        const n = [...initialQuotes]; n[idx].timeline_days = parseInt(e.target.value); setInitialQuotes(n);
                                    }} required />
                                    <div className="flex items-center gap-2">
                                        {q.attachment_url ? (
                                            <span className="text-xs text-emerald-600 font-semibold flex items-center gap-1">
                                                <CheckCircle2 size={14} /> PDF Attached
                                            </span>
                                        ) : (
                                            <label className="cursor-pointer bg-white border border-slate-200 rounded-md p-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1 justify-center w-full">
                                                <Upload size={14} /> Attach PDF
                                                <input
                                                    type="file"
                                                    accept=".pdf,image/*"
                                                    className="hidden"
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            try {
                                                                const url = await uploadFile(file, 'quotations');
                                                                const n = [...initialQuotes];
                                                                n[idx].attachment_url = url;
                                                                setInitialQuotes(n);
                                                            } catch (err) {
                                                                alert('Failed to upload PDF');
                                                            }
                                                        }
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button type="button" onClick={addQuoteField} className="text-xs font-bold text-violet-600 hover:text-violet-800">+ Add Vendor Quote</button>
                        </div>

                        <div className="flex justify-end pt-2">
                            <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-slate-800 transition-all">
                                Launch Tendering
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="space-y-6">
                {procs.map((p: any) => (
                    <div key={p.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                    <StatusBadge status={p.status} />
                                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                        <Clock size={10} /> Ends: {new Date(p.deadline).toLocaleDateString()}
                                    </span>
                                </div>
                                <h3 className="text-lg font-bold text-slate-900">{p.title}</h3>
                                <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {p.status === 'open' && (
                                    <button onClick={() => updateStatus(p.id, 'under_review')} className="px-3 py-1.5 bg-violet-50 text-violet-600 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-violet-100">
                                        Move to Review
                                    </button>
                                )}
                                {p.status === 'under_review' && (
                                    <button onClick={() => updateStatus(p.id, 'decided')} className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-100">
                                        Finalize Decision
                                    </button>
                                )}
                                <button className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-50/30">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-4">Quotations Trail ({p.quotations.length})</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {p.quotations.map((q: any) => (
                                    <div key={q.id} className={cn(
                                        "bg-white p-4 rounded-xl border transition-all relative",
                                        q.is_flagged ? "border-red-200 bg-red-50/20" : "border-slate-200"
                                    )}>
                                        {q.is_flagged && (
                                            <div className="absolute top-2 right-2 text-red-500" title={q.flag_reason}>
                                                <AlertTriangle size={16} />
                                            </div>
                                        )}
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-lg font-bold text-slate-900">₹{parseFloat(q.amount).toLocaleString()}</span>
                                            <span className="text-[10px] font-medium text-slate-400">{q.timeline_days} days</span>
                                        </div>
                                        <p className="text-xs text-slate-500 line-clamp-2 mb-4">"{q.remarks}"</p>
                                        <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-50">
                                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                                                <User size={12} /> {q.is_admin_quote ? 'Board' : 'Resident'}
                                            </div>
                                            {q.attachment_url && (
                                                <a href={q.attachment_url} target="_blank" rel="noreferrer" className="text-violet-600 hover:scale-110 transition-transform">
                                                    <ExternalLink size={14} />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProcurementManager;
